import { useQueryClient } from '@tanstack/react-query';
import { DateTime } from 'luxon';
import { useState } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';

import Button from '@/components/Button';
import ShowHideButton from '@/components/ShowHideButton';
import Text from '@/components/Text';
import useAlerts from '@/hooks/useAlerts';
import { api } from '@/utils/api';
import { CoverImagesProjectStatus } from '@/utils/enums';
import { RootState } from '@/utils/store';

const usersEmails = [
  'greta@boredpanda.com',
  'andrius.sadausk@gmail.com',
  'tony@boredpanda.com',
];

const Holder = styled.div`
  margin-bottom: 48px;
`;

const Box = styled.div`
  border-radius: 4px;
  border: 1px solid ${({ theme }) => theme.colors.lightGray};
  background: ${({ theme }) => theme.colors.white};
  margin-bottom: 24px;
`;

const BoxHeader = styled.div`
  border-bottom: 1px solid ${({ theme }) => theme.colors.grayscaleLight};
  padding: 28px 24px;
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const BoxInner = styled.div`
  position: relative;
  display: grid;
  grid-gap: 24px 48px;
  grid-template-columns: 1fr 1fr;
  padding: 24px;
`;

const Divider = styled.div`
  position: absolute;
  left: 50%;
  top: 24px;
  bottom: 24px;
  width: 1px;
  background-color: ${({ theme }) => theme.colors.lightGray};
`;

const Title = styled(Text)`
  line-height: 22px;
  margin-top: 6px;
`;

const ButtonHolder = styled.div`
  display: flex;
  justify-content: flex-end;
`;

const ArticleBox = () => {
  const [isPosting, setIsPosting] = useState(false);

  const [showContent, setShowContent] = useState(true);

  const queryClient = useQueryClient();

  const alerts = useAlerts();

  const navigate = useNavigate();

  const userEmail = useSelector((state: RootState) => state.auth.user?.email);

  const { title, date_created, editor, slug, id, is_open_list } = useSelector(
    (rootState: RootState) => rootState.coverImages,
  );

  if (!id) return <></>;

  const onAiGenerate = async () => {
    setIsPosting(true);
    try {
      const { projectId } = await api.post<any, { projectId: number }>(
        '/v1/cover-images/generate-ai-content',
        {
          article_title: title,
          article_id: id,
          is_open_list,
          slug,
          created_in_bp_at: date_created,
          editor_email: editor,
        },
      );

      await queryClient.invalidateQueries(['new_cover_images_projects'], {
        exact: false,
      });

      await api.post(`/v1/cover-images/send-slack-notif/${projectId}`, {
        is_firs_message: true,
        status: CoverImagesProjectStatus.PENDING,
      });

      navigate(`/general/cover-images/${projectId}`);

      alerts.success('Generating in progress');
    } catch (err) {
      alerts.error((err as any)?.response?.data?.message);
    }
    setIsPosting(false);
  };

  return (
    <Holder>
      <Box>
        <BoxHeader>
          <Text $type="button">Article Details</Text>
          <ShowHideButton
            showContent={showContent}
            onClick={() => setShowContent((prev) => !prev)}
          />
        </BoxHeader>
        {showContent && (
          <BoxInner>
            <Divider />
            <div>
              <Text $type="smallBold" $color="gray">
                Article Name
              </Text>
              <Title $type="caption">{title ? title : '-'}</Title>
            </div>
            <div>
              <Text $type="smallBold" $color="gray">
                Date Created
              </Text>
              <Title $type="caption">
                {date_created
                  ? DateTime.fromISO(date_created, {
                      zone: 'UTC',
                    }).toFormat('dd/LL/yyyy t')
                  : '-'}
              </Title>
            </div>
            <div>
              <Text $type="smallBold" $color="gray">
                Editor
              </Text>
              <Title $type="caption">{editor ? editor : '-'}</Title>
            </div>
          </BoxInner>
        )}
      </Box>
      {userEmail && usersEmails.includes(userEmail) && (
        <ButtonHolder>
          <Button
            loading={isPosting}
            disabled={isPosting}
            onClick={onAiGenerate}
          >
            AI Generate
          </Button>
        </ButtonHolder>
      )}
    </Holder>
  );
};

export default ArticleBox;
